import AppContext from 'App/AppContext';
import { IPCheckbox, IPScrollablePane, IPSpinner } from 'baseComponents';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import AddNewLink from 'components/Links/AddNewLink';
import LinkList from 'components/Links/LinkList';
import { canUpdateTaskField, TaskFieldTypes } from 'components/Tasks/TaskAuthHelper';
import { onRenderTextWithInfo } from 'globalFunctions';
import {
  globalStackItemStylesPaddingSceneScroll,
  globalStackStylesHeight100,
  globalStackTokensGapSmall,
} from 'globalStyles';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import Entity, { EntityTypes } from 'models/entity';
import ResourceLink from 'models/resourceLink';
import Task from 'models/tasks/task';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetLinksForId, apiGetLinksForTask } from 'services/Api/linkService';
import { apiRequest } from 'services/Auth/authConfig';
import { hasUserDataPermission } from 'services/Auth/featurePermissions';
import { getLocalStorageData, LocalStorageKeys, setLocalStorageData } from 'utils/localstorage';

interface ITaskLinkList {
  task: Task;
  taskLinks: ResourceLink[];
  hideWholeList?: boolean;
  navigateExternal?: boolean;
  fixedLinkIds?: number[];
  addLinks: (links: ResourceLink[], validateForm: boolean) => void;
  setLinks: (links: ResourceLink[], validateForm: boolean) => void;
  removeLink: (link: ResourceLink, validateForm: boolean) => void;
  onPin?: (item: ResourceLink, pinned: boolean) => void;
  // Locks the list on top of what the permissions already say, for a caller that knows the task is
  // frozen for a reason of its own (an audit execution task whose audit is completed). Left out, the
  // permissions decide on their own, as before.
  readonly?: boolean;
}

const TaskLinkList = (props: ITaskLinkList) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['task', 'library']);
  const [extraLinks, setExtraLinks] = useState<ResourceLink[]>([]);
  const [fixedLinks, setFixedLinks] = useState<ResourceLink[]>([]);
  const [taskId, setTaskId] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const hasPermission = useMemo(
    () => hasUserDataPermission(appContext, [props.task.authSchemaId], AuthSchemaLineOperation.Update),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.task.authSchemaId],
  );
  //derived, not state: props.readonly can arrive after the first render (the audit status behind an
  //execution task is loaded asynchronously), and a state initializer would never see it
  const readonly =
    props.readonly === true || !canUpdateTaskField(props.task, hasPermission, TaskFieldTypes.Library, appContext);

  const taskLinksDisplay = useMemo(() => {
    //filter extraLinks to not include links already in props.taskLinks or fixedLinks
    const filteredExtraLinks = extraLinks.filter(
      (el) =>
        !props.taskLinks.some((tl) => tl.linkId === el.linkId) && !fixedLinks.some((fl) => fl.linkId === el.linkId),
    );

    return [...props.taskLinks, ...fixedLinks, ...filteredExtraLinks];
  }, [extraLinks, fixedLinks, props.taskLinks]);

  const getShowAllStateFromStorage = () => {
    const showAll: boolean = getLocalStorageData(appContext, LocalStorageKeys.ShowAllLinks) === 'true';

    return showAll;
  };

  const [showAll, setShowAll] = useState<boolean>(getShowAllStateFromStorage());

  useEffect(() => {
    if (props.task.taskId !== taskId) {
      setTaskId(props.task.taskId);
      loadData(showAll);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task]);

  const loadData = async (showAll: boolean) => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      //Load the details of the task links in 1 call for:
      //- links that are not already loaded into props.tasklinks
      //- links that are not new (id <= 0)
      //- fixedLinkIds that are not already loaded into props.tasklinks
      let linksToGet =
        props.task.resourceLinkIds?.filter((id) => id > 0 && !props.taskLinks.some((t) => t.linkId === id)) ?? [];

      //also get links from fixedLinkIds that are not already loaded into props.tasklinks
      if (props.fixedLinkIds && props.fixedLinkIds.length > 0) {
        const fixedLinksToGet = props.fixedLinkIds.filter(
          (id) =>
            id > 0 && !props.taskLinks.some((t) => t.linkId === id && !linksToGet.some((linkId) => linkId === id)),
        );
        linksToGet = linksToGet.concat(fixedLinksToGet);
      }

      if (linksToGet.length > 0) {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const _links = await apiGetLinksForId(accessToken, linksToGet, appContext.globalDataCache);
        if (_links.length > 0) {
          //add links to the state of SingleTask
          //but exclude the fixed links because these are not part of this task but from the template
          //if we included them, they would be added to the task on save
          const linksToAdd = _links.filter((l) => !props.fixedLinkIds?.some((id) => id === l.linkId));
          props.addLinks(linksToAdd, true);
          //set fixed links separately
          const fixedLinksToSet = _links.filter((l) => props.fixedLinkIds?.some((id) => id === l.linkId));
          //set the entity on all fixed links with info that they cannot be removed
          fixedLinksToSet.forEach((l) => {
            l.entity = new Entity(l.linkId, EntityTypes.NotSet, t('library:LinkComponents.FixedLinkEntity'));
          });
          setFixedLinks(fixedLinksToSet);
        }
      }

      //async load extra links from other entities if showAll is true in a seperate call
      loadExtraLinks(showAll);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  const loadExtraLinks = async (showAll: boolean) => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      if (showAll) {
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const extraLinks = await apiGetLinksForTask(
          props.task.taskId,
          showAll,
          accessToken,
          appContext.globalDataCache,
        );
        //get the links from other entities
        setExtraLinks(extraLinks);
      } else {
        setExtraLinks([]);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  //
  // Main render
  //
  if (!props.task.taskId) {
    return null;
  }

  return (
    <IPStack verticalFill styles={globalStackStylesHeight100} tokens={globalStackTokensGapSmall}>
      <IPStack horizontal tokens={globalStackTokensGapSmall}>
        {!readonly && (
          <IPStack.Item>
            <AddNewLink
              addLinks={(links) => props.addLinks(links, true)}
              existingLinks={taskLinksDisplay}
              allowCustomLists={true}
              allowDocumentLibraries={true}
              allowWebURLs={true}
              allowSitePages={true}
              disabled={isLoading}
              entityContext={true}
              hideWholeList={props.hideWholeList}
              showCategoryContents={true}
            />
          </IPStack.Item>
        )}
        {props.task.taskId !== -1 && (
          <IPStack.Item>
            <IPCheckbox
              checked={showAll}
              labelElement={onRenderTextWithInfo(
                t('library:LinkComponents.ShowAllLinks'),
                t('library:LinkComponents.ShowAllLinksInfoTask'),
              )}
              onChange={(ev, checked) => {
                const showAll = checked || false;
                setLocalStorageData(appContext, LocalStorageKeys.ShowAllLinks, showAll.toString());
                setShowAll(showAll);
                loadExtraLinks(showAll);
              }}
            />
          </IPStack.Item>
        )}
      </IPStack>
      {isLoading && (
        <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
          <IPSpinner size={IPSpinnerSize.large} />
        </IPStack>
      )}
      {!isLoading && (
        <IPStack.Item grow styles={globalStackItemStylesPaddingSceneScroll}>
          <IPScrollablePane>
            <LinkList
              items={taskLinksDisplay}
              onDelete={(link) => props.removeLink(link, true)}
              onPin={props.onPin}
              navigateExternal={props.navigateExternal}
              readonly={readonly}
            />
          </IPScrollablePane>
        </IPStack.Item>
      )}
    </IPStack>
  );
};

export default TaskLinkList;
